'use client';

import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { EditModal } from '@/components/ui/edit-modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { replacePlayerAsManager } from '@/api/registrations/registrationsApi';
import { handleGlobalError } from '@/utils/error-handler';
import {
  ExistingPlayerSearch,
  UserAvatar,
} from './ExistingPlayerSearch';
import { hasEloForSport } from './utils';
import type { Player } from '@/types';
import type { User } from '@/types/user.model';

interface ReplacePlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  registrationId: string;
  /** Outgoing player (the one being replaced). */
  currentPlayer: Player;
  /** Emails of the rest of the team, to exclude them from the search. */
  teammateEmails: string[];
  /** Tournament sport: the replacement needs Elo for it or the backend rejects them. */
  sportUid: string;
  onReplaced: () => void;
}

export function ReplacePlayerModal({
  isOpen,
  onClose,
  tournamentId,
  registrationId,
  currentPlayer,
  teammateEmails,
  sportUid,
  onReplaced,
}: ReplacePlayerModalProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Can't replace with someone already on the team (including the outgoing player).
  const reservedEmails = useMemo(
    () =>
      new Set(
        [currentPlayer.email, ...teammateEmails].map((e) => e.toLowerCase()),
      ),
    [currentPlayer.email, teammateEmails],
  );

  // Unlike the team registration modal there is no category to pick here: this endpoint still applies
  // the tournament's category range, so assigning one on the fly could bounce anyway. We just say why.
  const missingElo = selected !== null && !hasEloForSport(selected, sportUid);

  const handleSubmit = async () => {
    if (!selected || missingElo) return;
    setSubmitting(true);
    try {
      await replacePlayerAsManager(
        tournamentId,
        registrationId,
        currentPlayer.email,
        selected.email,
      );
      toast({
        title: 'Jugador reemplazado',
        description: `${selected.name || selected.email} ahora forma parte del equipo.`,
      });
      onReplaced();
      onClose();
    } catch (err) {
      handleGlobalError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reemplazar jugador"
      onSave={handleSubmit}
      isLoading={submitting}
      disabled={!selected || missingElo}
      size="default"
      mobileFullScreen
    >
      <div className="flex flex-col gap-6 py-4">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold">Jugador a reemplazar</p>
          <div className="flex items-center gap-3 rounded-md border border-border bg-gray-50 p-3">
            <UserAvatar user={currentPlayer} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {currentPlayer.name || currentPlayer.email}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {currentPlayer.email}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold">Jugador entrante</p>
          {selected ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 rounded-md bg-green-50 p-3">
                <UserAvatar user={selected} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-green-900">
                    {selected.name || selected.email}
                  </p>
                  <p className="truncate text-xs text-green-700">
                    {selected.email}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelected(null);
                    setQuery('');
                  }}
                >
                  <X className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Cambiar</span>
                </Button>
              </div>

              {missingElo && (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Este jugador no tiene categoría para el deporte del torneo:
                  nunca respondió las preguntas de nivel en la app. Sin ELO no se
                  puede armar el fixture, así que no puede entrar al equipo hasta
                  que la cargue.
                </p>
              )}
            </div>
          ) : (
            <ExistingPlayerSearch
              id="replacement-search"
              label="Buscar jugador entrante"
              helper="Solo jugadores con cuenta en GetMatch. El backend valida que cumpla las restricciones del torneo."
              placeholder="Nombre o email del jugador"
              query={query}
              onQueryChange={setQuery}
              reservedEmails={reservedEmails}
              onPick={(user) => {
                setSelected(user);
                setQuery('');
              }}
            />
          )}
        </div>
      </div>
    </EditModal>
  );
}
